import { useState, useEffect, useRef } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
	RefreshCw,
	Loader2,
	Info,
	CheckCircle,
	AlertTriangle,
	Clock,
	Database,
	FileText,
	Table2,
	Users,
	ChevronDown,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { getAdminOrganizations, getOrganizationUsers } from "@/services/api";
import { useToast } from "@/components/ui/use-toast";
import {
	previewRecalculation,
	applyRecalculation,
	getRecalculationStatus,
} from "@/services/recalculation";
import type { RecalculateRequest, PreviewResult, JobStatus } from "@/services/recalculation";

type ActionType = "attendance" | "sessionSummaries" | "dailyReports" | "timesheets";

const ORG_TZ = "Australia/Sydney";

/**
 * Format a UTC timestamp for display in org timezone.
 * Shows ⁺¹ indicator on clock-out when it falls on the next calendar day.
 * Clock-in never shows +1 (it's confusing — the employee started on "this day").
 */
function formatClockTime(
	utcIso: string | null,
	attendanceDate: string,
	showDayOffset: boolean,
): string {
	if (!utcIso) return "—";
	const dt = new Date(utcIso);
	const time = dt.toLocaleTimeString("en-AU", {
		hour: "2-digit",
		minute: "2-digit",
		hour12: true,
		timeZone: ORG_TZ,
	});
	if (!showDayOffset) return time;
	// Compare calendar day of the timestamp vs attendance date in org timezone
	const tsDay = new Date(
		dt.toLocaleString("en-US", { timeZone: ORG_TZ }),
	).getDate();
	const attDay = Number.parseInt(attendanceDate.split("-")[2], 10);
	if (tsDay !== attDay) {
		return `${time} ⁺¹`;
	}
	return time;
}

interface FormData {
	organizationId: string;
	employeeId: string;
	startDate: string;
	endDate: string;
	actions: ActionType[];
}

function buildRequest(form: FormData): RecalculateRequest {
	return {
		organizationId: form.organizationId,
		employeeIds: form.employeeId && form.employeeId !== "all" ? [form.employeeId] : [],
		startDate: form.startDate,
		endDate: form.endDate,
		actions: form.actions,
	};
}

function ProgressBar({ value, max }: { value: number; max: number }) {
	const pct = max > 0 ? Math.round((value / max) * 100) : 0;
	return (
		<div className="w-full bg-gray-200 rounded-full h-2.5">
			<div
				className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
				style={{ width: `${pct}%` }}
			/>
		</div>
	);
}

function JobStatusPanel({ jobId }: { jobId: string }) {
	const [status, setStatus] = useState<JobStatus | null>(null);
	const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

	useEffect(() => {
		let active = true;

		async function poll() {
			try {
				const result = await getRecalculationStatus(jobId);
				if (active) {
					setStatus(result);
					if (result.status === "completed" || result.status === "failed") {
						if (intervalRef.current) clearInterval(intervalRef.current);
					}
				}
			} catch {
				// swallow poll errors, keep trying
			}
		}

		poll();
		intervalRef.current = setInterval(poll, 2000);

		return () => {
			active = false;
			if (intervalRef.current) clearInterval(intervalRef.current);
		};
	}, [jobId]);

	if (!status) {
		return (
			<div className="flex items-center gap-2 text-gray-500">
				<Loader2 className="w-4 h-4 animate-spin" />
				<span>Waiting for job status...</span>
			</div>
		);
	}

	if (status.status === "failed") {
		return (
			<div className="space-y-4">
				<div className="flex items-center gap-2 text-red-600">
					<AlertTriangle className="w-5 h-5" />
					<span className="font-medium">Job Failed</span>
				</div>
				{status.failedReason && (
					<p className="text-sm text-red-500">{status.failedReason}</p>
				)}
			</div>
		);
	}

	if (status.status === "completed" && status.results) {
		return (
			<div className="space-y-4">
				<div className="p-4 bg-green-50 rounded-lg text-center">
					<CheckCircle className="w-10 h-10 mx-auto mb-2 text-green-500" />
					<p className="font-medium text-green-700">Recalculation Complete</p>
				</div>
				<div className="space-y-2">
					{status.results.attendanceDays && (
						<div className="flex items-center justify-between p-3 bg-gray-50 rounded">
							<div className="flex items-center gap-2">
								<Clock className="w-4 h-4 text-blue-500" />
								<span>Attendance days recalculated</span>
							</div>
							<div className="flex gap-2">
								<Badge>{status.results.attendanceDays.recalculated}</Badge>
								{status.results.attendanceDays.skipped > 0 && (
									<Badge variant="outline">{status.results.attendanceDays.skipped} skipped</Badge>
								)}
								{status.results.attendanceDays.errors > 0 && (
									<Badge variant="destructive">{status.results.attendanceDays.errors} errors</Badge>
								)}
							</div>
						</div>
					)}
					{status.results.sessionSummaries && (
						<div className="flex items-center justify-between p-3 bg-gray-50 rounded">
							<div className="flex items-center gap-2">
								<Database className="w-4 h-4 text-blue-500" />
								<span>Session summaries</span>
							</div>
							<div className="flex gap-2">
								<Badge>{status.results.sessionSummaries.changed} changed</Badge>
								<Badge variant="outline">{status.results.sessionSummaries.processed} processed</Badge>
							</div>
						</div>
					)}
					{status.results.dailyReports && (
						<div className="flex items-center justify-between p-3 bg-gray-50 rounded">
							<div className="flex items-center gap-2">
								<FileText className="w-4 h-4 text-amber-500" />
								<span>Daily reports regenerated</span>
							</div>
							<Badge>{status.results.dailyReports.regenerated}</Badge>
						</div>
					)}
					{status.results.timesheets && (
						<div className="flex items-center justify-between p-3 bg-gray-50 rounded">
							<div className="flex items-center gap-2">
								<Table2 className="w-4 h-4 text-green-500" />
								<span>Timesheets regenerated</span>
							</div>
							<div className="flex gap-2">
								<Badge>{status.results.timesheets.regenerated}</Badge>
								{status.results.timesheets.skippedLocked > 0 && (
									<Badge variant="outline">{status.results.timesheets.skippedLocked} locked</Badge>
								)}
							</div>
						</div>
					)}
				</div>
			</div>
		);
	}

	// In progress
	const progress = status.progress;
	return (
		<div className="space-y-4">
			<div className="flex items-center gap-2 text-blue-600">
				<Loader2 className="w-4 h-4 animate-spin" />
				<span className="font-medium">
					{progress?.phase ?? "Processing..."}
				</span>
			</div>
			{progress && progress.total > 0 && (
				<div className="space-y-1">
					<ProgressBar value={progress.current} max={progress.total} />
					<p className="text-xs text-gray-500 text-right">
						{progress.current} / {progress.total}
					</p>
				</div>
			)}
		</div>
	);
}

function EmployeeSelector({
	users,
	selectedId,
	disabled,
	onChange,
}: {
	users: Array<{ _id: string; email: string; firstName?: string; lastName?: string }>;
	selectedId: string;
	disabled: boolean;
	onChange: (id: string) => void;
}) {
	const [search, setSearch] = useState("");
	const [open, setOpen] = useState(false);
	const ref = useRef<HTMLDivElement>(null);

	// Close on outside click
	useEffect(() => {
		function handleClick(e: MouseEvent) {
			if (ref.current && !ref.current.contains(e.target as Node)) {
				setOpen(false);
			}
		}
		document.addEventListener("mousedown", handleClick);
		return () => document.removeEventListener("mousedown", handleClick);
	}, []);

	const filtered = users.filter((u) => {
		if (!search) return true;
		const q = search.toLowerCase();
		const name = `${u.firstName || ""} ${u.lastName || ""}`.toLowerCase();
		return name.includes(q) || u.email.toLowerCase().includes(q);
	});

	const selectedUser = users.find((u) => u._id === selectedId);
	const displayText = selectedId
		? selectedUser
			? `${selectedUser.firstName || ""} ${selectedUser.lastName || ""}`.trim() || selectedUser.email
			: "Selected"
		: "All Employees";

	return (
		<div ref={ref} className="relative">
			<Label>Employee (optional)</Label>
			<div
				className={`mt-2 flex items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm cursor-pointer ${disabled ? "opacity-50 pointer-events-none" : ""}`}
				onClick={() => !disabled && setOpen(!open)}
			>
				<span className={selectedId ? "text-foreground" : "text-muted-foreground"}>
					{displayText}
				</span>
				<ChevronDown className="h-4 w-4 text-muted-foreground" />
			</div>
			{open && (
				<div className="absolute z-50 mt-1 w-full rounded-md border bg-white shadow-lg max-h-64 overflow-hidden">
					<div className="p-2 border-b">
						<input
							type="text"
							placeholder="Search by name or email..."
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm outline-none focus:ring-1 focus:ring-ring"
							autoFocus
						/>
					</div>
					<div className="max-h-48 overflow-y-auto">
						<div
							className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 ${!selectedId ? "bg-blue-50 font-medium" : ""}`}
							onClick={() => { onChange(""); setOpen(false); setSearch(""); }}
						>
							All Employees
						</div>
						{filtered.map((user) => {
							const name = `${user.firstName || ""} ${user.lastName || ""}`.trim();
							return (
								<div
									key={user._id}
									className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 ${selectedId === user._id ? "bg-blue-50 font-medium" : ""}`}
									onClick={() => { onChange(user._id); setOpen(false); setSearch(""); }}
								>
									<div>{name || user.email}</div>
									{name && <div className="text-xs text-gray-400">{user.email}</div>}
								</div>
							);
						})}
						{filtered.length === 0 && (
							<div className="px-3 py-4 text-sm text-gray-400 text-center">No employees found</div>
						)}
					</div>
				</div>
			)}
		</div>
	);
}

export function RecalculationTool() {
	const { toast } = useToast();
	const [formData, setFormData] = useState<FormData>({
		organizationId: "",
		employeeId: "",
		startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
			.toISOString()
			.split("T")[0],
		endDate: new Date().toISOString().split("T")[0],
		actions: ["attendance", "sessionSummaries", "dailyReports", "timesheets"],
	});
	const [jobId, setJobId] = useState<string | null>(null);

	const { data: organizations } = useQuery({
		queryKey: ["organizations-list"],
		queryFn: () => getAdminOrganizations({ limit: 100 }),
	});

	const { data: usersData } = useQuery({
		queryKey: ["org-users", formData.organizationId],
		queryFn: () => getOrganizationUsers(formData.organizationId, { limit: 100 }),
		enabled: !!formData.organizationId,
	});

	const previewMutation = useMutation({
		mutationFn: previewRecalculation,
		onError: (error: Error) => {
			toast({
				title: "Preview Failed",
				description: error.message || "Failed to preview recalculation",
				variant: "destructive",
			});
		},
	});

	const applyMutation = useMutation({
		mutationFn: applyRecalculation,
		onSuccess: (data) => {
			setJobId(data.jobId);
		},
		onError: (error: Error) => {
			toast({
				title: "Apply Failed",
				description: error.message || "Failed to start recalculation",
				variant: "destructive",
			});
		},
	});

	function toggleAction(action: ActionType) {
		setFormData((prev) => ({
			...prev,
			actions: prev.actions.includes(action)
				? prev.actions.filter((a) => a !== action)
				: [...prev.actions, action],
		}));
	}

	function handlePreview() {
		if (!formData.organizationId) {
			toast({
				title: "Error",
				description: "Please select an organization",
				variant: "destructive",
			});
			return;
		}
		if (formData.actions.length === 0) {
			toast({
				title: "Error",
				description: "Please select at least one action",
				variant: "destructive",
			});
			return;
		}
		setJobId(null);
		previewMutation.mutate(buildRequest(formData));
	}

	function handleApply() {
		applyMutation.mutate(buildRequest(formData));
	}

	const preview: PreviewResult | undefined = previewMutation.data;

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
						<RefreshCw className="w-6 h-6" />
						Recalculate Data
					</h1>
					<p className="text-gray-500 mt-1">
						Recalculate session summaries, daily reports, and timesheets
					</p>
				</div>
			</div>

			{/* Info Banner */}
			<div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
				<Info className="w-6 h-6 text-blue-600 flex-shrink-0" />
				<div>
					<p className="font-medium text-blue-900">
						This tool recalculates derived data using current app classifications. Preview changes before applying.
					</p>
				</div>
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
				{/* Configuration */}
				<Card>
					<CardHeader>
						<CardTitle>Configuration</CardTitle>
					</CardHeader>
					<CardContent className="space-y-6">
						{/* Organization */}
						<div>
							<Label>Organization</Label>
							<Select
								value={formData.organizationId}
								onValueChange={(value) =>
									setFormData({ ...formData, organizationId: value, employeeId: "" })
								}
							>
								<SelectTrigger className="mt-2">
									<SelectValue placeholder="Select an organization" />
								</SelectTrigger>
								<SelectContent>
									{organizations?.organizations?.map((org) => (
										<SelectItem key={org._id} value={org._id}>
											{org.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						{/* Employee */}
						<EmployeeSelector
							users={usersData?.users || []}
							selectedId={formData.employeeId}
							disabled={!formData.organizationId}
							onChange={(id) => setFormData({ ...formData, employeeId: id })}
						/>

						{/* Date Range */}
						<div className="grid grid-cols-2 gap-4">
							<div>
								<Label>Start Date</Label>
								<input
									type="date"
									value={formData.startDate}
									onChange={(e) =>
										setFormData({ ...formData, startDate: e.target.value })
									}
									className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
								/>
							</div>
							<div>
								<Label>End Date</Label>
								<input
									type="date"
									value={formData.endDate}
									onChange={(e) =>
										setFormData({ ...formData, endDate: e.target.value })
									}
									className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
								/>
							</div>
						</div>

						{/* Actions */}
						<div className="space-y-3">
							<Label>Actions</Label>
							<div className="flex items-start gap-3 p-3 rounded-lg border">
								<Checkbox
									id="action-attendance"
									checked={formData.actions.includes("attendance")}
									onCheckedChange={() => toggleAction("attendance")}
								/>
								<div>
									<label htmlFor="action-attendance" className="text-sm font-medium cursor-pointer">
										Attendance
									</label>
									<p className="text-xs text-gray-500">Recalculate attendance status, late/early/OT metrics</p>
								</div>
							</div>
							<div className="flex items-start gap-3 p-3 rounded-lg border">
								<Checkbox
									id="action-sessions"
									checked={formData.actions.includes("sessionSummaries")}
									onCheckedChange={() => toggleAction("sessionSummaries")}
								/>
								<div>
									<label htmlFor="action-sessions" className="text-sm font-medium cursor-pointer">
										Session Summaries
									</label>
									<p className="text-xs text-gray-500">Re-classify app usage with current rules</p>
								</div>
							</div>
							<div className="flex items-start gap-3 p-3 rounded-lg border">
								<Checkbox
									id="action-daily"
									checked={formData.actions.includes("dailyReports")}
									onCheckedChange={() => toggleAction("dailyReports")}
								/>
								<div>
									<label htmlFor="action-daily" className="text-sm font-medium cursor-pointer">
										Daily Reports
									</label>
									<p className="text-xs text-gray-500">Regenerate from corrected session data</p>
								</div>
							</div>
							<div className="flex items-start gap-3 p-3 rounded-lg border">
								<Checkbox
									id="action-timesheets"
									checked={formData.actions.includes("timesheets")}
									onCheckedChange={() => toggleAction("timesheets")}
								/>
								<div>
									<label htmlFor="action-timesheets" className="text-sm font-medium cursor-pointer">
										Timesheets
									</label>
									<p className="text-xs text-gray-500">Regenerate draft timesheets only</p>
								</div>
							</div>
						</div>

						{/* Buttons */}
						<Button
							className="w-full"
							onClick={handlePreview}
							disabled={previewMutation.isPending || !formData.organizationId}
						>
							{previewMutation.isPending ? (
								<Loader2 className="w-4 h-4 mr-2 animate-spin" />
							) : (
								<RefreshCw className="w-4 h-4 mr-2" />
							)}
							Preview Changes
						</Button>

						{preview && !jobId && (
							<Button
								className="w-full"
								variant="destructive"
								onClick={handleApply}
								disabled={applyMutation.isPending}
							>
								{applyMutation.isPending ? (
									<Loader2 className="w-4 h-4 mr-2 animate-spin" />
								) : (
									<RefreshCw className="w-4 h-4 mr-2" />
								)}
								Apply Recalculation
							</Button>
						)}
					</CardContent>
				</Card>

				{/* Results */}
				<Card>
					<CardHeader>
						<CardTitle>
							{jobId ? "Job Progress" : preview ? "Preview Results" : "Results"}
						</CardTitle>
					</CardHeader>
					<CardContent>
						{jobId ? (
							<JobStatusPanel jobId={jobId} />
						) : preview ? (
							<PreviewPanel preview={preview} />
						) : (
							<div className="text-center py-12 text-gray-500">
								<Database className="w-12 h-12 mx-auto mb-4 opacity-50" />
								<p>Configure and preview to see what will change</p>
							</div>
						)}
					</CardContent>
				</Card>
			</div>
		</div>
	);
}

function PreviewPanel({ preview }: { preview: PreviewResult }) {
	const { employeeSummary, attendanceDays, sessionSummaries, dailyReports, timesheets } = preview;

	return (
		<div className="space-y-6">
			{/* Employee Summary */}
			{employeeSummary && employeeSummary.length > 0 && (
				<div className="space-y-3">
					<div className="flex items-center gap-2">
						<Users className="w-4 h-4 text-purple-500" />
						<h3 className="font-medium">Employee Details</h3>
						<Badge variant="secondary">{employeeSummary.length} employees</Badge>
					</div>
					<div className="overflow-x-auto">
						<table className="w-full text-xs border-collapse">
							<thead>
								<tr className="bg-gray-50">
									<th className="text-left p-2 border-b">Employee</th>
									<th className="text-left p-2 border-b">Type</th>
									<th className="text-left p-2 border-b">Shift</th>
									<th className="text-right p-2 border-b">Break</th>
									<th className="text-right p-2 border-b">Grace</th>
									<th className="text-left p-2 border-b">Productive Source</th>
									<th className="text-left p-2 border-b">Salary Basis</th>
									<th className="text-right p-2 border-b">Sessions</th>
								</tr>
							</thead>
							<tbody>
								{employeeSummary.map((emp) => (
									<tr key={emp.employeeId} className="border-b last:border-0 hover:bg-gray-50">
										<td className="p-2">
											<div className="font-medium truncate max-w-[120px]">{emp.employeeName}</div>
											<div className="text-gray-400 truncate max-w-[120px]">{emp.email}</div>
										</td>
										<td className="p-2">
											<Badge variant={emp.contractType === "contract" ? "outline" : "secondary"} className="text-[10px]">
												{emp.contractType}
											</Badge>
										</td>
										<td className="p-2">
											{emp.shiftName ? (
												<div>
													<div className="truncate max-w-[140px]">{emp.shiftName}</div>
													{emp.shiftTimes && <div className="text-gray-400">{emp.shiftTimes}</div>}
												</div>
											) : (
												<span className="text-red-500">No shift</span>
											)}
										</td>
										<td className="p-2 text-right">{emp.breakAllowanceMin}m</td>
										<td className="p-2 text-right">{emp.gracePeriodMin}m</td>
										<td className="p-2">
											<Badge
												variant={emp.useProductiveHours ? "default" : "outline"}
												className="text-[10px]"
											>
												{emp.useProductiveHours ? "Productive" : "Clock"}
											</Badge>
										</td>
										<td className="p-2">{emp.salaryBasis}</td>
										<td className="p-2 text-right font-medium">{emp.sessionsInRange}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			)}

			{attendanceDays && (
				<div className="space-y-3">
					<div className="flex items-center gap-2">
						<Clock className="w-4 h-4 text-blue-500" />
						<h3 className="font-medium">Attendance Days</h3>
						<Badge variant={attendanceDays.toRecalculate > 0 ? "destructive" : "secondary"}>
							{attendanceDays.toRecalculate} to recalculate
						</Badge>
						{attendanceDays.manuallySkipped > 0 && (
							<Badge variant="outline">
								{attendanceDays.manuallySkipped} manually corrected (skipped)
							</Badge>
						)}
					</div>
					{attendanceDays.records.length > 0 && (
						<div className="overflow-x-auto">
							<table className="w-full text-xs border-collapse">
								<thead>
									<tr className="bg-gray-50">
										<th className="text-left p-2 border-b">Employee</th>
										<th className="text-left p-2 border-b">Date</th>
										<th className="text-left p-2 border-b">Clock In</th>
										<th className="text-left p-2 border-b">Clock Out</th>
										<th className="text-left p-2 border-b">Status</th>
										<th className="text-right p-2 border-b">Worked</th>
										<th className="text-right p-2 border-b">Late</th>
										<th className="text-right p-2 border-b">OT</th>
										<th className="text-right p-2 border-b">Break</th>
										<th className="text-left p-2 border-b">Shift Window (UTC)</th>
										<th className="text-center p-2 border-b">Changed?</th>
									</tr>
								</thead>
								<tbody>
									{attendanceDays.records.map((r, i) => (
										<tr key={i} className={`border-b last:border-0 ${r.manuallyCorrected ? "bg-yellow-50 opacity-60" : r.changed ? "bg-red-50" : "hover:bg-gray-50"}`}>
											<td className="p-2 truncate max-w-[100px]">{r.employeeName}</td>
											<td className="p-2">{r.date}</td>
											<td className="p-2 text-gray-600">
												{formatClockTime(r.clockIn, r.date, false)}
											</td>
											<td className="p-2 text-gray-600">
												{formatClockTime(r.clockOut, r.date, true)}
											</td>
											<td className="p-2">
												<div className="flex items-center gap-1">
													<Badge variant={r.before.status === "Present" ? "default" : r.before.status === "Late" ? "destructive" : "outline"} className="text-[10px]">{r.before.status}</Badge>
													{r.after && r.before.status !== r.after.status && (
														<><span className="text-gray-400">→</span><Badge variant={r.after.status === "Present" ? "default" : r.after.status === "Late" ? "destructive" : "outline"} className="text-[10px]">{r.after.status}</Badge></>
													)}
												</div>
											</td>
											<td className="p-2 text-right">
												{Math.floor(r.before.workedMin / 60)}h {r.before.workedMin % 60}m
												{r.after && Math.abs(r.before.workedMin - r.after.workedMin) > 1 && (
													<div className="text-green-600 font-medium">→ {Math.floor(r.after.workedMin / 60)}h {r.after.workedMin % 60}m</div>
												)}
											</td>
											<td className="p-2 text-right">
												{r.before.lateMin > 0 ? `${r.before.lateMin}m` : "—"}
												{r.after && Math.abs(r.before.lateMin - r.after.lateMin) > 1 && (
													<div className="text-green-600 font-medium">→ {r.after.lateMin > 0 ? `${r.after.lateMin}m` : "0"}</div>
												)}
											</td>
											<td className="p-2 text-right">
												{r.before.otMin > 0 ? `${r.before.otMin}m` : "—"}
												{r.after && Math.abs(r.before.otMin - r.after.otMin) > 1 && (
													<div className="text-green-600 font-medium">→ {r.after.otMin > 0 ? `${r.after.otMin}m` : "0"}</div>
												)}
											</td>
											<td className="p-2 text-right">
												{r.before.breakMin > 0 ? `${r.before.breakMin}m` : "—"}
												{r.after && Math.abs(r.before.breakMin - r.after.breakMin) > 1 && (
													<div className="text-green-600 font-medium">→ {r.after.breakMin > 0 ? `${r.after.breakMin}m` : "0"}</div>
												)}
											</td>
											<td className="p-2 font-mono text-[10px] whitespace-nowrap">
												<div className="text-gray-600">
													{r.before.windowStartUtc ? r.before.windowStartUtc.slice(5, 16).replace("T", " ") : "—"}
													{" → "}
													{r.before.windowEndUtc ? r.before.windowEndUtc.slice(5, 16).replace("T", " ") : "—"}
												</div>
												{r.after && (r.before.windowStartUtc !== r.after.windowStartUtc || r.before.windowEndUtc !== r.after.windowEndUtc) && (
													<div className="text-green-600 font-medium">
														↓ {r.after.windowStartUtc ? r.after.windowStartUtc.slice(5, 16).replace("T", " ") : "—"}
														{" → "}
														{r.after.windowEndUtc ? r.after.windowEndUtc.slice(5, 16).replace("T", " ") : "—"}
													</div>
												)}
												{r.error && (
													<div className="text-red-600 text-[10px] mt-0.5">⚠ {r.error}</div>
												)}
											</td>
											<td className="p-2 text-center">
												{r.manuallyCorrected ? (
													<Badge variant="outline" className="text-[10px] text-yellow-600">manual</Badge>
												) : r.error ? (
													<Badge variant="destructive" className="text-[10px]">ERROR</Badge>
												) : r.changed ? (
													<Badge variant="destructive" className="text-[10px]">YES</Badge>
												) : (
													<span className="text-gray-400">—</span>
												)}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</div>
			)}

			{sessionSummaries && (
				<div className="space-y-3">
					<div className="flex items-center gap-2">
						<Database className="w-4 h-4 text-blue-500" />
						<h3 className="font-medium">Session Summaries</h3>
						<Badge variant={sessionSummaries.willChange > 0 ? "destructive" : "secondary"}>
							{sessionSummaries.willChange} of {sessionSummaries.total} will change
						</Badge>
					</div>
					{sessionSummaries.samples.length > 0 && (
						<div className="overflow-x-auto">
							<table className="w-full text-xs border-collapse">
								<thead>
									<tr className="bg-gray-50">
										<th className="text-left p-2 border-b">Employee</th>
										<th className="text-left p-2 border-b">Date</th>
										<th className="text-right p-2 border-b">Duration</th>
										<th className="text-right p-2 border-b">Productive</th>
										<th className="text-right p-2 border-b">Unprod.</th>
										<th className="text-right p-2 border-b">Neutral</th>
										<th className="text-right p-2 border-b">Idle</th>
										<th className="text-right p-2 border-b">Break</th>
										<th className="text-left p-2 border-b">Clock In</th>
										<th className="text-left p-2 border-b">Clock Out</th>
									</tr>
								</thead>
								<tbody>
									{sessionSummaries.samples.map((s) => (
										<tr key={s.sessionId} className="border-b last:border-0 hover:bg-gray-50">
											<td className="p-2 truncate max-w-[100px]">{s.employeeName}</td>
											<td className="p-2">{s.date}</td>
											<td className="p-2 text-right">{s.sessionDurationH.toFixed(1)}h</td>
											<td className="p-2 text-right">
												<span className="text-gray-500">{s.before.productiveH.toFixed(1)}h</span>
												{Math.abs(s.before.productiveH - s.after.productiveH) > 0.05 && (
													<div className="text-green-600 font-medium">→ {s.after.productiveH.toFixed(1)}h</div>
												)}
											</td>
											<td className="p-2 text-right">
												<span className="text-gray-500">{s.before.unproductiveH.toFixed(1)}h</span>
												{Math.abs(s.before.unproductiveH - s.after.unproductiveH) > 0.05 && (
													<div className="text-green-600 font-medium">→ {s.after.unproductiveH.toFixed(1)}h</div>
												)}
											</td>
											<td className="p-2 text-right">
												<span className="text-gray-500">{s.before.neutralH.toFixed(1)}h</span>
												{Math.abs(s.before.neutralH - s.after.neutralH) > 0.05 && (
													<div className="text-green-600 font-medium">→ {s.after.neutralH.toFixed(1)}h</div>
												)}
											</td>
											<td className="p-2 text-right text-gray-500">{s.before.idleH.toFixed(1)}h</td>
											<td className="p-2 text-right text-gray-500">{s.before.breakH.toFixed(1)}h</td>
											<td className="p-2 text-xs text-gray-500">{s.clockIn ? new Date(s.clockIn).toLocaleTimeString() : "—"}</td>
											<td className="p-2 text-xs text-gray-500">{s.clockOut ? new Date(s.clockOut).toLocaleTimeString() : "—"}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</div>
			)}

			{dailyReports && (
				<div className="space-y-3">
					<div className="flex items-center gap-2">
						<FileText className="w-4 h-4 text-amber-500" />
						<h3 className="font-medium">Daily Reports</h3>
						<Badge variant={dailyReports.toRegenerate > 0 ? "destructive" : "secondary"}>
							{dailyReports.toRegenerate} to regenerate
						</Badge>
					</div>
					{dailyReports.records.length > 0 && (
						<div className="overflow-x-auto">
							<table className="w-full text-xs border-collapse">
								<thead>
									<tr className="bg-gray-50">
										<th className="text-left p-2 border-b">Employee</th>
										<th className="text-left p-2 border-b">Date</th>
										<th className="text-center p-2 border-b">Score</th>
										<th className="text-right p-2 border-b">Work</th>
										<th className="text-right p-2 border-b">Productive</th>
										<th className="text-right p-2 border-b">Unprod.</th>
										<th className="text-right p-2 border-b">Idle</th>
										<th className="text-right p-2 border-b">Break</th>
										<th className="text-right p-2 border-b">Offline</th>
										<th className="text-left p-2 border-b">In</th>
										<th className="text-left p-2 border-b">Out</th>
									</tr>
								</thead>
								<tbody>
									{dailyReports.records.map((r, i) => (
										<tr key={i} className="border-b last:border-0 hover:bg-gray-50">
											<td className="p-2 truncate max-w-[100px]">{r.employeeName}</td>
											<td className="p-2">{r.date}</td>
											<td className="p-2 text-center">
												<span className="font-medium">{r.currentScore}%</span>
												<Badge variant={r.currentGrade === "A" || r.currentGrade === "B" ? "default" : r.currentGrade === "F" ? "destructive" : "outline"} className="text-[10px] ml-1">{r.currentGrade}</Badge>
											</td>
											<td className="p-2 text-right">{r.totalWorkTimeH.toFixed(1)}h</td>
											<td className={`p-2 text-right font-medium ${r.productiveTimeH > r.totalWorkTimeH ? "text-red-600" : "text-gray-900"}`}>{r.productiveTimeH.toFixed(1)}h{r.productiveTimeH > r.totalWorkTimeH && " ⚠️"}</td>
											<td className="p-2 text-right text-red-600">{r.unproductiveTimeH > 0 ? `${r.unproductiveTimeH.toFixed(1)}h` : "—"}</td>
											<td className="p-2 text-right text-gray-500">{r.idleTimeH > 0 ? `${r.idleTimeH.toFixed(1)}h` : "—"}</td>
											<td className="p-2 text-right text-amber-600">{r.breakTimeH > 0 ? `${r.breakTimeH.toFixed(1)}h` : "—"}</td>
											<td className="p-2 text-right text-gray-400">{r.offlineTimeH > 0 ? `${r.offlineTimeH.toFixed(1)}h` : "—"}</td>
											<td className="p-2 text-xs text-gray-500">{r.clockIn ? new Date(r.clockIn).toLocaleTimeString() : "—"}</td>
											<td className="p-2 text-xs text-gray-500">{r.clockOut ? new Date(r.clockOut).toLocaleTimeString() : "—"}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</div>
			)}

			{timesheets && (
				<div className="space-y-3">
					<div className="flex items-center gap-2">
						<Table2 className="w-4 h-4 text-green-500" />
						<h3 className="font-medium">Timesheets</h3>
						<Badge variant={timesheets.draftsToRegenerate > 0 ? "destructive" : "secondary"}>
							{timesheets.draftsToRegenerate} drafts to regenerate
						</Badge>
						<Badge variant="outline">
							{timesheets.lockedSkipped} locked (skipped)
						</Badge>
					</div>
					{timesheets.records.length > 0 && (
						<div className="overflow-x-auto">
							<table className="w-full text-xs border-collapse">
								<thead>
									<tr className="bg-gray-50">
										<th className="text-left p-2 border-b">Employee</th>
										<th className="text-left p-2 border-b">Period</th>
										<th className="text-left p-2 border-b">Status</th>
										<th className="text-right p-2 border-b">Worked</th>
										<th className="text-right p-2 border-b">Productive</th>
										<th className="text-right p-2 border-b">OT</th>
										<th className="text-right p-2 border-b">Days</th>
									</tr>
								</thead>
								<tbody>
									{timesheets.records.map((r, i) => (
										<tr key={i} className={`border-b last:border-0 ${r.status === "draft" ? "hover:bg-gray-50" : "bg-yellow-50 opacity-60"}`}>
											<td className="p-2 truncate max-w-[100px]">{r.employeeName}</td>
											<td className="p-2 text-xs">{r.period}</td>
											<td className="p-2">
												<Badge variant={r.status === "draft" ? "outline" : r.status === "approved" ? "default" : "secondary"} className="text-[10px]">{r.status}</Badge>
											</td>
											<td className="p-2 text-right">{r.totalWorkedH.toFixed(1)}h</td>
											<td className="p-2 text-right text-green-700">{r.totalProductiveH.toFixed(1)}h</td>
											<td className="p-2 text-right">{r.totalOvertimeH > 0 ? `${r.totalOvertimeH.toFixed(1)}h` : "—"}</td>
											<td className="p-2 text-right">{r.daysWorked}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</div>
			)}

			{!attendanceDays && !sessionSummaries && !dailyReports && !timesheets && (
				<div className="text-center py-8 text-gray-500">
					<CheckCircle className="w-10 h-10 mx-auto mb-2 text-gray-400" />
					<p>No data matched the selected criteria</p>
				</div>
			)}
		</div>
	);
}
